import { jsx } from 'react/jsx-runtime';
import clsx from 'clsx';
import { forwardRef, useRef, useState, useEffect, useMemo, Children, cloneElement } from 'react';
import { TabsSizesXL } from '../sizes/index.js';
import { useTabsStyles } from '../styles/index.js';
import { computeResponsiveness } from '../../../../foundation/responsive/helpers/index.js';

const Tabs = forwardRef(({ children, size = TabsSizesXL, value = 0, onChange, isFixed, className, dataTestId, ...props }, ref) => {
    const sizes = computeResponsiveness(size);
    const tabsRef = useRef(null);
    const [activeTabUI, setActiveTabUI] = useState({ x: 0, width: 0 });
    const [activeTab, setActiveTab] = useState(value);
    useEffect(() => setActiveTab(value), [value]);
    const [isAnimate, setIsAnimate] = useState(false);
    useEffect(() => {
        const parent = tabsRef.current;
        const activeChild = parent?.children[activeTab];
        if (parent && activeChild) {
            const updateActiveTabUI = () => {
                const childWidth = activeChild.clientWidth;
                if (activeChild instanceof HTMLElement) {
                    setActiveTabUI({ x: activeChild.offsetLeft, width: childWidth });
                }
            };
            const resizeObserver = new ResizeObserver(updateActiveTabUI);
            resizeObserver.observe(activeChild);
            updateActiveTabUI();
            if (typeof activeChild.scrollIntoView === 'function') {
                activeChild.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
            const setActiveIndicatorColor = () => {
                parent.setAttribute('active-tab-hovered', '');
            };
            const removeActiveIndicatorColor = () => {
                parent.removeAttribute('active-tab-hovered');
            };
            activeChild.addEventListener('mouseenter', setActiveIndicatorColor);
            activeChild.addEventListener('mouseleave', removeActiveIndicatorColor);
            return () => {
                resizeObserver.disconnect();
                activeChild.removeEventListener('mouseenter', setActiveIndicatorColor);
                activeChild.removeEventListener('mouseleave', removeActiveIndicatorColor);
            };
        }
    }, [activeTab]);
    const memoizedActiveTabUI = useMemo(() => activeTabUI, [activeTabUI]);
    const styles = useTabsStyles({ sizes, activeTabUI: memoizedActiveTabUI, isFixed });
    const handleTabClick = (idx) => {
        setActiveTab(idx);
        setIsAnimate(true);
        if (onChange) {
            onChange(idx);
        }
    };
    return (jsx("div", { ...props, "data-testid": dataTestId || 'tabs', className: clsx(styles.root, className), ref: ref, children: jsx("div", { className: styles.tabList, ref: tabsRef, children: Children.map(children, (child, index) => (child
                ? (child && cloneElement(child, {
                    isActive: index === activeTab,
                    onClick: () => handleTabClick(index),
                    size,
                    isFixed,
                    // eslint-disable-next-line react/no-array-index-key
                    key: index,
                    index,
                    isAnimate,
                })) : null)) }) }));
});
Tabs.displayName = 'Tabs';

export { Tabs };
